import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    LOCALE_ID,
    Provider,
    importProvidersFrom,
    inject,
    provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, withViewTransitions } from '@angular/router';

import { ConfigurationService as CheckoutConfigurationService, CheckoutModule, Oauth2Service } from '@bytel/checkout';
import { AngularPrismeLogger } from '@bytel/prisme-logger/lib/angular';

import { ProductHelper } from '@base/catalog/product-helper';
import { PhonePipe } from '@base/common/pipes/phone.pipe';
import { CacheHttpInterceptor } from '@base/common/services/cache-http.interceptor';
import { MocksHandlerModule } from '@base/mocks-handler/mocks-handler.module';

import { ConfigurationService } from '@services/configuration.service';
import { PrismeErrorService } from '@services/prisme-error.service';

import { PaymentResolver } from '@tunnel/payment/payment.resolver';

import { IInitialConfig } from '@interfaces/config.interface';

import { PriceInitialPipe } from '@shared/pipes/price-initial.pipe';

import { routes } from './app.routes';

const CONFIG: () => IInitialConfig = () => window.ConfigInitial;

export const getPrismeLoggerInterceptorProvider = (): Provider => ({
    provide: HTTP_INTERCEPTORS,
    useFactory: (prismeLogger: AngularPrismeLogger) => prismeLogger.getInterceptor(),
    multi: true,
    deps: [AngularPrismeLogger],
});

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withViewTransitions()),
        importProvidersFrom(BrowserModule, MocksHandlerModule, CommonModule, CheckoutModule.forRoot(CONFIG)),
        PriceInitialPipe,
        ProductHelper,
        PaymentResolver,
        provideAppInitializer(() => inject(CheckoutConfigurationService).initialize(inject(ConfigurationService).getData()).pipe(() => inject(Oauth2Service).autoLogin())),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheHttpInterceptor,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: 'fr-FR',
        },
        PhonePipe,
        {
            provide: APP_BASE_HREF,
            useValue: '/',
        },
        {
            provide: AngularPrismeLogger,
            useFactory: (): AngularPrismeLogger =>
                new AngularPrismeLogger({
                    st: window.ConfigInitial.oauth.xSource,
                    environnement: window.ConfigInitial.environment,
                    urlEndpoint: window.ConfigInitial.oauth.resourceUrl + '/traces-techniques',
                    tailleMaxEnvoiQueueMessages: 5,
                }),
        },
        getPrismeLoggerInterceptorProvider(),
        {
            provide: ErrorHandler,
            useClass: PrismeErrorService,
        },
        provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
    ],
};
